import { Component, computed, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslocoModule } from '@jsverse/transloco';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { filter, map, startWith } from 'rxjs';
import { RequestFormService } from '../../../shared/services/request-form.service';

export type HeaderVariant = 'home' | 'stepper' | 'detail' | 'cancel' | 'confirm' | 'list';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [TranslocoModule, MatToolbarModule, MatButtonModule, MatProgressBarModule, FontAwesomeModule],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class HeaderComponent {
  private readonly router = inject(Router);
  readonly formService = inject(RequestFormService);
  readonly faArrowLeft = faArrowLeft;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects),
      startWith(this.router.url)
    )
  );

  readonly variant = computed<HeaderVariant>(() => {
    const url = this.currentUrl() ?? '';
    if (url.includes('nuova-richiesta') || url.includes('modifica-richiesta')) return 'stepper';
    if (url.includes('dettaglio-richiesta')) return 'detail';
    if (url.includes('conferma-annulla-richiesta')) return 'confirm';
    if (url.includes('annulla-richiesta')) return 'cancel';
    if (url.includes('storico-richieste')) return 'list';
    return 'home';
  });

  readonly progressPercent = computed(() =>
    (this.formService.currentStep() / this.formService.totalSteps) * 100
  );

  private readonly requestId = computed(() => {
    const url = this.currentUrl() ?? '';
    const match = url.match(/\/(?:dettaglio-richiesta|modifica-richiesta|annulla-richiesta)\/([^/?#]+)/);
    return match?.[1] ?? null;
  });

  goBack(): void {
    switch (this.variant()) {
      case 'stepper':
        this.goBackFromStepper();
        return;
      case 'detail':
        this.goBackFromDetail();
        return;
      case 'cancel':
        this.goBackFromCancel();
        return;
      case 'list':
        this.router.navigate(['/home']);
        return;
      default:
        history.back();
    }
  }

  private goBackFromStepper(): void {
    this.formService.goBack();
  }

  private goBackFromCancel(): void {
    const id = this.requestId();
    this.router.navigate(id ? ['/dettaglio-richiesta', id] : ['/home']);
  }

  // Il dettaglio è raggiungibile sia da home che dallo storico richieste: il query param "from",
  // impostato da chi naviga qui, dice all'header dove tornare indietro
  private goBackFromDetail(): void {
    const url = this.currentUrl() ?? '';
    this.router.navigate([url.includes('from=storico') ? '/storico-richieste' : '/home']);
  }
}
